import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import ServiceWrapper from "../components/ServiceWrapper";
import Modal from "../components/Modal";
import auth from "../utils/auth";
import { track } from "../utils/track";
import { useScoreCount } from "../utils/scoreCount";
import { onIap, requestIapInfo, startAppSubscription } from "../utils/iap";

/**
 * 앱 안 구독 (2026-10-10 사장님: 앱에서 처음 만난 사람이 그 자리에서 바로 구독).
 * 안드로이드 앱 2.1.0+ 에서 /pricing 대신 이 화면 — 결제는 구글 플레이, 가격은 웹과 같은 월 3,300원.
 * 디자인은 웹 구독 안내(Pricing.tsx)와 같게.
 */
const FEATURES = [
  "마디 보기 · 가사 보기(노래방식) · 박자 표시",
  "키 바꾸기 · 쉬운 코드(카포) · 코드 단순하게",
  "기타 반주 · 메트로놈 · 탭 템포",
  "연주 체크와 점수 · 원곡 영상 같이 보기",
  "신청곡 악보 제작 (악보 신청)",
];

const AppSubscribe: React.FC = () => {
  const history = useHistory();
  const scoreCount = useScoreCount();
  const status = auth.getPayStatus();
  const subscribed = status === "1" || status === "0" || status === "4";
  const [price, setPrice] = useState("3,300원");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const off = onIap((e) => {
      if (e.type === "info") {
        if (e.price) setPrice(e.price.replace(/^₩\s?/, "") + (/원$/.test(e.price) ? "" : "원"));
        return;
      }
      setBusy(false);
      if (e.type === "done") {
        track("iap:done");
        setDone(true);
      } else if (e.message) {
        setErr(e.message);
      }
    });
    requestIapInfo();
    return off;
  }, []);

  const start = async () => {
    track("iap:start");
    if (!auth.getToken()) {
      history.push("/auth/login");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      await startAppSubscription();
    } catch (e) {
      setBusy(false);
      setErr("구독을 시작하지 못했어요. 잠시 뒤 다시 시도해 주세요.");
    }
  };

  return (
    <ServiceWrapper isFooterRendered={false}>
      <Page>
        <h1>코드스코어 구독</h1>
        <Lead>치고 싶은 노래, 코드 악보로 바로.</Lead>

        <Plan className="p-plan">
          <PlanHead>
            <span>월 구독</span>
            <Price>
              <b>{price}</b>
              <small>/ 월</small>
            </Price>
          </PlanHead>
          <ul>
            {[`${scoreCount}곡이 넘는 코드 악보를 모두 열람`, ...FEATURES].map((f) => (
              <li key={f}>
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l4 4 10-10" /></svg>
                {f}
              </li>
            ))}
          </ul>
          <Period>
            구글 플레이로 결제해요. 결제가 끝나면 바로 모든 악보를 볼 수 있어요.
            <br />
            해지하지 않으면 매월 같은 날 자동으로 결제되어 1개월씩 이어집니다.
          </Period>
          <Start type="button" onClick={start} disabled={subscribed || busy}>
            {subscribed ? "이미 구독 중이에요" : busy ? "결제 창을 여는 중…" : auth.getToken() ? "구독 시작하기" : "로그인하고 구독하기"}
          </Start>
          {err && <Err>{err}</Err>}
        </Plan>

        <Notes className="p-notes">
          <h2>결제 · 해지 · 환불 안내</h2>
          <ul>
            <li>결제와 자동 갱신은 구글 플레이 계정으로 이루어져요.</li>
            <li>해지는 구글 플레이 스토어 › 프로필 › 결제 및 정기 결제 › 정기 결제 에서 할 수 있어요. 해지해도 이번 결제 기간이 끝날 때까지 이용할 수 있어요.</li>
            <li>환불은 구글 플레이 환불 정책에 따라요. 도움이 필요하면 <Link to="/support">문의하기</Link>로 남겨 주세요.</li>
          </ul>
          <p>
            자세한 내용은 <Link to="/policy/service">이용약관</Link>과 <Link to="/policy/privacy">개인정보 처리방침</Link>을 확인해 주세요.
          </p>
        </Notes>
      </Page>
      <Modal
        open={done}
        onClose={() => window.location.replace("/")}
        firstButtonLabel="악보 보러 가기"
        onClickFirstButton={() => window.location.replace("/")}
        title="구독 완료"
        content={<>구독해 주셔서 고마워요!<br />지금부터 모든 악보를 볼 수 있어요.</>}
      />
    </ServiceWrapper>
  );
};

export default AppSubscribe;

const Err = styled.p`
  margin: 0;
  font-size: 14px;
  color: #ff8a8a;
  line-height: 1.5;
`;

const Page = styled.div`
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 24px 20px 110px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  color: #f3f4f6;
  font-family: "Noto Sans KR", "Apple SD Gothic Neo", sans-serif;
  & > h1 {
    margin: 0;
    font-size: 26px;
    font-weight: 800;
  }
  @media (min-width: 700px) {
    padding: 40px 32px 60px;
  }
  @media (min-width: 1024px) {
    max-width: 1080px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 48px;
    align-items: start;
    & > h1,
    & > p,
    & > footer {
      grid-column: 1 / -1;
    }
    & > .p-notes {
      padding-top: 8px;
    }
  }
`;

const Lead = styled.p`
  margin: -12px 0 0;
  font-size: 15px;
  color: #a3a9b5;
`;

const Plan = styled.section`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 22px 20px;
  border-radius: 20px;
  background: #16181e;
  border: 1px solid #3a3220;
  & > ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  & li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 15px;
    line-height: 1.45;
    word-break: keep-all;
  }
  & li > svg {
    flex: none;
    width: 18px;
    height: 18px;
    margin-top: 2px;
    fill: none;
    stroke: #ffb547;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const PlanHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  & > span {
    font-size: 13px;
    color: #ffb547;
    font-weight: 700;
  }
`;

const Price = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  & > b {
    font-size: 34px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }
  & > small {
    font-size: 14px;
    color: #a3a9b5;
  }
`;

const Period = styled.p`
  margin: 0;
  padding: 12px 14px;
  border-radius: 12px;
  background: #1f222a;
  font-size: 14px;
  line-height: 1.6;
  color: #d5d9e0;
`;

const Start = styled.button`
  height: 54px;
  border: 0;
  border-radius: 14px;
  background: #ffb547;
  color: #0d0e12;
  font-size: 17px;
  font-weight: 800;
  cursor: pointer;
  &:disabled {
    background: #2a2e38;
    color: #a3a9b5;
    cursor: default;
  }
`;

const Notes = styled.section`
  font-size: 14px;
  line-height: 1.6;
  color: #c7cbd3;
  word-break: keep-all;
  & > h2 {
    margin: 0 0 10px;
    font-size: 17px;
    color: #f3f4f6;
  }
  & > ul {
    margin: 0 0 10px;
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  & a {
    color: #8fb0ff;
  }
  & > p {
    margin: 0;
  }
`;
